import {IMappingResponse} from '../../../../api/types/ingestionApi.type';
import {GridFormValues} from '../gridSchema';

/**
 * Validate mandatory mapping rows have all required fields.
 * Returns an object with ok flag and message (empty when ok).
 */
export function validateMandatoryMappings(
  mappings: GridFormValues['columnMappings']
): {ok: boolean; message: string} {
  if (!mappings || mappings.length === 0) {
    return {
      ok: false,
      message: 'No column mappings found. Please configure mappings.',
    };
  }
  const incomplete = mappings.filter(
    (m) =>
      m.mandatory &&
      (!m.sourceFieldID ||
        !m.typeID ||
        m.typeID === 'Select' ||
        !m.destinationID ||
        m.destinationID === 0)
  );
  if (incomplete.length > 0) {
    return {
      ok: false,
      message:
        'All mandatory rows must have Source Field, Type, and Destination Field selected.',
    };
  }
  return {ok: true, message: ''};
}

export const getDuplicateDestinationNames = (
  mappings: GridFormValues['columnMappings']
): string[] => {
  const seenIds = new Map<number, string>();
  const duplicates = new Set<string>();

  mappings.forEach((item) => {
    const id = item.destinationID;
    const name = item.destinationText;

    if (seenIds.has(id ?? 0)) {
      duplicates.add(seenIds.get(id ?? 0)!);
      duplicates.add(name ?? '');
    } else {
      seenIds.set(id ?? 0, name ?? '');
    }
  });

  return Array.from(duplicates).filter(Boolean);
};
/**
 * Detect if any non-mandatory rows are partially filled (1 or 2 fields only).
 */
export function hasIncompleteNonMandatoryRows(
  mappings: GridFormValues['columnMappings']
): boolean {
  if (!mappings) return false;
  return (
    mappings
      .filter((m) => !m.mandatory)
      .filter((m) => {
        const hasSource = !!m.sourceFieldID;
        const hasType = !!m.typeID && m.typeID !== 'Select';
        const hasDest = !!m.destinationID && m.destinationID !== 0;
        const filledCount = [hasSource, hasType, hasDest].filter(
          Boolean
        ).length;
        return filledCount > 0 && filledCount < 3;
      }).length > 0
  );
}

/**
 * Return only:
 *  - mandatory rows
 *  - non-mandatory rows that are fully completed (all 3 mapping fields)
 */
export function filterValidMappingsForPreviewAndDraft(
  mappings: GridFormValues['columnMappings']
) {
  if (!mappings) return [];
  return mappings.filter((m) => {
    if (m.mandatory) return true;
    const hasSource = !!m.sourceFieldID;
    const hasType = !!m.typeID && m.typeID !== 'Select';
    const hasDest = !!m.destinationID && m.destinationID !== 0;
    return hasSource && hasType && hasDest;
  });
}

export interface IngestionMappingModel {
  sequenceNo: string;
  sourceFieldID: string;
  selectedSourceField: string;
  selectedTypeID: number;
  selectedType: string;
  selectedDestinationID: number;
  destination: string;
  isMandatory: boolean;
  isNewRow: boolean;
  clientUniqueIdentifierRow: number;
  sourceField: {value: string; text: string};
  type: {
    value: string;
    text: string;
    destinationField: Array<{value: number; text: string}>;
  };
}

/**
 * Build ingestion mapping models (same format used in preview).
 * NOTE: For parity with existing implementation selectedTypeID is set to destinationID.
 * If you prefer the real type ID, change `selectedTypeID: mapping.destinationID ?? 0`
 * to: `selectedTypeID: Number(mapping.typeID ?? 0)`.
 */
export function buildIngestionMappingModels(
  mappings: GridFormValues['columnMappings'],
  mappingData: IMappingResponse | null
): IngestionMappingModel[] {
  const filtered = filterValidMappingsForPreviewAndDraft(mappings);

  return filtered.map((mapping, index) => {
    const sourceFieldObj =
      mappingData?.sourceField.find(
        (sf) => sf.value === String(mapping.sourceFieldID)
      ) || null;

    const typeObj =
      mappingData?.type.find((t) => t.value === mapping.typeID) || null;

    return {
      sequenceNo: String(index + 1),
      sourceFieldID: String(mapping.sourceFieldID ?? ''),
      selectedSourceField: mapping.sourceFieldText ?? '',
      selectedTypeID: mapping.destinationID ?? 0, // keep parity
      selectedType: mapping.typeText ?? '',
      selectedDestinationID: mapping.destinationID ?? 0,
      destination: mapping.destinationText ?? '',
      isMandatory: mapping.mandatory,
      isNewRow: mapping.isNewRow,
      clientUniqueIdentifierRow: mapping.clientUniqueIdentifierRow,
      sourceField: {
        value: sourceFieldObj?.value ?? '',
        text: sourceFieldObj?.text ?? '',
      },
      type: {
        value: typeObj?.value ?? '',
        text: typeObj?.text ?? '',
        destinationField: typeObj?.destinationField ?? [],
      },
    };
  });
}

/**
 * Convenience wrapper for draft save usage.
 */
export function buildDraftIngestionMappingPayload(
  gridValues: GridFormValues,
  mappingData: IMappingResponse | null
): IngestionMappingModel[] {
  return buildIngestionMappingModels(gridValues.columnMappings, mappingData);
}
